// AgentListRow — catalog list rows on /agents, aligned with /models `ModelListRow`
// (wide tabular row + XS stacked card).

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import { AGENT_ICON } from '@/constants/config';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import useFavoriteAgent from '@/hooks/useFavoriteAgent';
import useCopyAgentLink from '@/hooks/useCopyAgentLink';
import AgentOwnerMenu from '@/components/Agents/AgentOwnerMenu';

export const AGENT_LIST_COLS = [
  { id: 'name', flex: 1, minWidth: 220 },
  { id: 'favorite', width: 80, align: 'right' },
  { id: 'chat', width: 88, align: 'right' },
];

const cellStyleFor = (c) => {
  if (c.flex) return { flex: c.flex, minWidth: c.minWidth || 0, paddingRight: 12 };
  return { width: c.width };
};

const stop = (e) => {
  e?.stopPropagation?.();
  if (typeof e?.preventDefault === 'function') e.preventDefault();
};

const Avatar = ({ agent, size, colors }) => (
  <View
    style={{
      width: size,
      height: size,
      borderRadius: Math.round(size / 4),
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    }}
  >
    {agent.avatarPath ? (
      <Image accessibilityLabel="" source={{ uri: agent.avatarPath }} style={{ width: size, height: size }} contentFit="cover" />
    ) : (
      <Icon name={AGENT_ICON} size={Math.round(size * 0.42)} color={colors.text_color_secondary} />
    )}
  </View>
);

const NameMeta = ({ agent, colors, onCopyLink, copyLabel }) => {
  const isPlus = !!agent?.flags?.isPlusOnly;
  return (
    <View style={{ flex: 1, minWidth: 0 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <Text size="s" weight="semibold" numberOfLines={1} style={{ flexShrink: 1 }}>{agent.name}</Text>
        {isPlus && <Text size="xxs" weight="semibold" color="secondary">PLUS</Text>}
        {agent?.isOwner && onCopyLink ? (
          <Pressable
            onPress={(e) => {
              e?.stopPropagation?.();
              onCopyLink(agent);
            }}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={copyLabel}
            style={Platform.OS === 'web' ? { cursor: 'pointer' } : undefined}
          >
            <Icon name="link" size={11} color={colors.text_color_tertiary} />
          </Pressable>
        ) : null}
      </View>
      <Text size="xxs" color="tertiary" numberOfLines={1}>{agent.description || ' '}</Text>
    </View>
  );
};

const FavoriteStar = ({ agentId, colors, size = 13 }) => {
  const { isSaved, toggleFavorite } = useFavoriteAgent(agentId);
  const { t } = useTranslation();
  return (
    <Tooltip label={t(isSaved ? 'pages.agents.removeFavoriteTooltip' : 'pages.agents.addFavoriteTooltip')}>
      <Pressable
        onPress={toggleFavorite}
        accessibilityRole="button"
        accessibilityState={{ selected: isSaved }}
        accessibilityLabel={isSaved
          ? (t('nav.routes.agentChat.unsave') || 'Remove from favorites')
          : (t('pages.agents.colFavorite') || 'Save')}
        style={({ hovered, pressed }) => [
          {
            width: 32,
            height: 32,
            borderRadius: 8,
            alignItems: 'center',
            justifyContent: 'center',
            ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
          },
          (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
        ]}
      >
        <Icon
          name="star"
          solid={isSaved}
          size={size}
          color={isSaved ? colors.text_color_primary : colors.text_color_tertiary}
        />
      </Pressable>
    </Tooltip>
  );
};

const AgentListRow = ({ agent, isXS, isLoading, onPress, onUseAgent }) => {
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuJustClosedRef = useRef(false);
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { copyAgentLink } = useCopyAgentLink();

  const handleMenuStateChange = useCallback((open) => {
    setIsMenuOpen(open);
    if (!open) {
      menuJustClosedRef.current = true;
      setTimeout(() => {
        menuJustClosedRef.current = false;
      }, 350);
    }
  }, []);

  const handleRowPress = useCallback((e) => {
    if (isMenuOpen || menuJustClosedRef.current) return;
    onPress?.(agent);
  }, [onPress, agent, isMenuOpen]);

  const handleCopyLink = useCallback((target) => {
    copyAgentLink(target || agent);
  }, [copyAgentLink, agent]);

  const handleUseAgentPress = useCallback((e) => {
    e?.stopPropagation?.();
    if (typeof e?.preventDefault === 'function') e.preventDefault();
    onUseAgent?.(agent);
  }, [onUseAgent, agent]);

  const handleMobileUsePress = useCallback((e) => {
    e?.stopPropagation?.();
    onUseAgent?.(agent);
  }, [onUseAgent, agent]);

  if (isLoading) {
    return (
      <View
        style={[
          styles.row,
          {
            minHeight: 64,
            paddingHorizontal: 16,
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: colors.border_color_secondary,
          },
        ]}
      >
        <View style={{ flexDirection: 'row', gap: 10, flex: 1, alignItems: 'center' }}>
          <View style={[styles.skel, { width: 32, height: 32, borderRadius: 8 }]} />
          <View style={{ flex: 1, gap: 6 }}>
            <View style={[styles.skel, { width: '42%', height: 12 }]} />
            <View style={[styles.skel, { width: '68%', height: 10 }]} />
          </View>
        </View>
      </View>
    );
  }

  if (!agent) return null;

  if (isXS) {
    return (
      <Pressable
        onPress={handleRowPress}
        style={[
          styles.mobileCard,
          {
            backgroundColor: hovered ? palette.hover : palette.surface,
            borderColor: palette.border,
          },
        ]}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        android_ripple={{ color: colors.solid_color_tertiary }}
      >
        <View style={styles.mobileHeader}>
          <Avatar agent={agent} size={40} colors={colors} />
          <NameMeta agent={agent} colors={colors} onCopyLink={handleCopyLink} copyLabel={t('pages.agents.copyLink')} />
          {!agent.isOwner && <FavoriteStar agentId={agent.id} colors={colors} />}
          <AgentOwnerMenu
            agent={agent}
            size={32}
            iconSize={13}
            onMenuStateChange={handleMenuStateChange}
          />
        </View>
        {onUseAgent ? (
          <Pressable
            onPress={handleMobileUsePress}
            style={[
              styles.mobileCta,
              {
                backgroundColor: palette.preview,
                borderWidth: 1,
                borderColor: palette.border,
              },
            ]}
            accessibilityRole="button"
            accessibilityLabel={t('pages.agents.useThisAgent')}
          >
            <Text size="xxs" weight="bold" color="secondary">{t('pages.agents.useThisAgent')}</Text>
            <Icon name="arrow-right" size={9} color={colors.text_color_secondary} />
          </Pressable>
        ) : null}
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={handleRowPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.row,
        {
          backgroundColor: hovered ? palette.hover : 'transparent',
          borderBottomColor: palette.border,
        },
        Platform.OS === 'web' && { cursor: 'pointer' },
      ]}
    >
      <View style={[styles.cell, cellStyleFor(AGENT_LIST_COLS[0]), { gap: 10 }]}>
        <Avatar agent={agent} size={40} colors={colors} />
        <NameMeta agent={agent} colors={colors} onCopyLink={handleCopyLink} copyLabel={t('pages.agents.copyLink')} />
      </View>
      <View
        style={[styles.cell, cellStyleFor(AGENT_LIST_COLS[1]), { justifyContent: 'flex-end', gap: 2 }]}
        onClick={stop}
        onPointerDown={stop}
        onMouseDown={stop}
      >
        {agent?.isOwner ? (
          <AgentOwnerMenu
            agent={agent}
            size={32}
            iconSize={13}
            onMenuStateChange={handleMenuStateChange}
          />
        ) : null}
        {!agent.isOwner && <FavoriteStar agentId={agent.id} colors={colors} />}
      </View>
      <View style={[styles.cell, cellStyleFor(AGENT_LIST_COLS[2]), { justifyContent: 'flex-end' }]}>
        {onUseAgent ? (
          <Pressable
            onPress={handleUseAgentPress}
            style={({ hovered: h }) => [
              styles.chatBtn,
              { borderColor: palette.border, backgroundColor: palette.surface },
              h && Platform.OS === 'web' ? { backgroundColor: palette.hover } : null,
            ]}
            accessibilityRole="button"
            accessibilityLabel={t('pages.agents.useThisAgent')}
          >
            <Icon name="comment-dots" size={12} color={colors.text_color_secondary} />
          </Pressable>
        ) : null}
      </View>
    </Pressable>
  );
};

AgentListRow.propTypes = {
  agent: PropTypes.object,
  isXS: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  onPress: PropTypes.func,
  onUseAgent: PropTypes.func,
};

const createStyles = (colors) => StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 64,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  cell: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  skel: {
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 4,
  },
  mobileCard: {
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
    gap: 10,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  mobileCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
    borderRadius: 8,
    alignSelf: 'stretch',
  },
  chatBtn: {
    width: 36,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
});

export default React.memo(AgentListRow);
